import { AccountService } from "./accountService";
import { ConsentService } from "./consentService";
import { TransactionService } from "./transactionService";
import { BankInfoService } from "./bankInfoService";
import { InvoiceService } from "./invoiceService";
import { ApprovalService } from "./approvalService";
import { PaymentService } from "./paymentService";
import { ReceivableService } from "./receivableService";
import { ManualMatchService } from './manualMatchService';
import { CompanyService } from "./companyService";
import { CompanySettingsService } from "./companySettingsService";
import { KycService } from "./kycService";
import { FiscalYearService } from "./fiscalYearService";
import { ChartOfAccountsService } from "./chartOfAccountsService";
import { BookkeepingSuggestionService } from "./bookkeepingSuggestionService";
import { MatchReviewService } from "./matchReviewService";
import { Sie4ExportService } from "./sie4ExportService";
import { ExpenseService } from './expenseService';
import { EmployeeService } from './employeeService';
import { ReceiptService } from './receiptService';
import { SalesReportService } from './salesReportService';
import { InvoiceUploadService } from './invoiceUploadService';
import { BookkeepingQaService } from "./bookkeepingQaService";
import { DocumentExportService } from "./documentExportService";
import { MessagingService } from "./messagingService";
import { EmailThreadService } from "./emailThreadService";
import { FeedbackService } from "./feedbackService";
import { ProductEventsService } from "./productEventsService";
import { CardService } from "./cardService";

/**
 * Registry for all service singletons
 * This ensures we use the same service instances throughout the application
 */
export class ServiceRegistry {
  private static accountService: AccountService;
  private static consentService: ConsentService;
  private static transactionService: TransactionService;
  private static bankInfoService: BankInfoService;
  private static invoiceService: InvoiceService;
  private static approvalService: ApprovalService;
  private static paymentService: PaymentService;
  private static receivableService: ReceivableService;
  private static manualMatchService: ManualMatchService;
  private static companyService: CompanyService;
  private static companySettingsService: CompanySettingsService;
  private static kycService: KycService;
  private static fiscalYearService: FiscalYearService;
  private static chartOfAccountsService: ChartOfAccountsService;
  private static bookkeepingSuggestionService: BookkeepingSuggestionService;
  private static matchReviewService: MatchReviewService;
  private static sie4ExportService: Sie4ExportService;
  private static expenseService: ExpenseService;
  private static employeeService: EmployeeService;
  private static receiptService: ReceiptService;
  private static salesReportService: SalesReportService;
  private static invoiceUploadService: InvoiceUploadService;
  private static bookkeepingQaService: BookkeepingQaService;
  private static documentExportService: DocumentExportService;
  private static messagingService: MessagingService;
  private static emailThreadService: EmailThreadService;
  private static feedbackService: FeedbackService;
  private static productEventsService: ProductEventsService;
  private static cardService: CardService;

  /**
   * Get or create the AccountService singleton
   */
  public static getAccountService(): AccountService {
    if (!this.accountService) {
      this.accountService = new AccountService();
    }
    return this.accountService;
  }

  /**
   * Get or create the ConsentService singleton
   */
  public static getConsentService(): ConsentService {
    if (!this.consentService) {
      this.consentService = new ConsentService();
    }
    return this.consentService;
  }

  /**
   * Get or create the TransactionService singleton
   */
  public static getTransactionService(): TransactionService {
    if (!this.transactionService) {
      this.transactionService = new TransactionService();
    }
    return this.transactionService;
  }

  /**
   * Get or create the BankInfoService singleton
   */
  public static getBankInfoService(): BankInfoService {
    if (!this.bankInfoService) {
      this.bankInfoService = new BankInfoService();
    }
    return this.bankInfoService;
  }
  

  /**
   * Get or create the InvoiceService singleton
   */
  public static getInvoiceService(): InvoiceService {
    if (!this.invoiceService) {
      this.invoiceService = new InvoiceService();
    }
    return this.invoiceService;
  }

  /**
   * Get or create the ApprovalService singleton
   */
  public static getApprovalService(): ApprovalService {
    if (!this.approvalService) {
      this.approvalService = new ApprovalService();
    }
    return this.approvalService;
  }

  /**
   * Get or create the PaymentService singleton
   */
  public static getPaymentService(): PaymentService {
    if (!this.paymentService) {
      this.paymentService = new PaymentService();
    }
    return this.paymentService;
  }

  /**
   * Get or create the ReceivableService singleton
   */
  public static getReceivableService(): ReceivableService {
    if (!this.receivableService) {
      this.receivableService = new ReceivableService();
    }
    return this.receivableService;
  }

  public static getSalesReportService(): SalesReportService {
    if (!this.salesReportService) {
      this.salesReportService = new SalesReportService();
    }
    return this.salesReportService;
  }

  /**
   * Get or create the ManualMatchService singleton
   */
  public static getManualMatchService(): ManualMatchService {
    if (!this.manualMatchService) {
      this.manualMatchService = new ManualMatchService();
    }
    return this.manualMatchService;
  }

  /**
   * Get or create the CompanyService singleton
   */
  public static getCompanyService(): CompanyService {
    if (!this.companyService) {
      this.companyService = new CompanyService();
    }
    return this.companyService;
  }

  /**
   * Get or create the CompanySettingsService singleton
   */
  public static getCompanySettingsService(): CompanySettingsService {
    if (!this.companySettingsService) {
      this.companySettingsService = new CompanySettingsService();
    }
    return this.companySettingsService;
  }

  /**
   * Get or create the KycService singleton
   */
  public static getKycService(): KycService {
    if (!this.kycService) {
      this.kycService = new KycService();
    }
    return this.kycService;
  }

  /**
   * Get or create the FiscalYearService singleton
   */
  public static getFiscalYearService(): FiscalYearService {
    if (!this.fiscalYearService) {
      this.fiscalYearService = new FiscalYearService();
    }
    return this.fiscalYearService;
  }
  
  /**
   * Get or create the ChartOfAccountsService singleton
   */
  public static getChartOfAccountsService(): ChartOfAccountsService {
    if (!this.chartOfAccountsService) {
      this.chartOfAccountsService = new ChartOfAccountsService();
    }
    return this.chartOfAccountsService;
  }

  /**
   * Get or create the BookkeepingSuggestionService singleton
   */
  public static getBookkeepingSuggestionService(): BookkeepingSuggestionService {
    if (!this.bookkeepingSuggestionService) {
      this.bookkeepingSuggestionService = new BookkeepingSuggestionService();
    }
    return this.bookkeepingSuggestionService;
  }

  /**
   * Get or create the MatchReviewService singleton
   */
  public static getMatchReviewService(): MatchReviewService {
    if (!this.matchReviewService) {
      this.matchReviewService = new MatchReviewService();
    }
    return this.matchReviewService;
  }

  /**
   * Get or create the Sie4ExportService singleton
   */
  public static getSie4ExportService(): Sie4ExportService {
    if (!this.sie4ExportService) {
      this.sie4ExportService = new Sie4ExportService();
    }
    return this.sie4ExportService;
  }

  public static getExpenseService(): ExpenseService {
    if (!ServiceRegistry.expenseService) {
      ServiceRegistry.expenseService = new ExpenseService();
    }
    return ServiceRegistry.expenseService;
  }

  public static getEmployeeService(): EmployeeService {
    if (!this.employeeService) {
      this.employeeService = new EmployeeService();
    }
    return this.employeeService;
  }

  public static getReceiptService(): ReceiptService {
    if (!this.receiptService) {
      this.receiptService = new ReceiptService();
    }
    return this.receiptService;
  }

  public static getInvoiceUploadService(): InvoiceUploadService {
    if (!this.invoiceUploadService) {
      this.invoiceUploadService = new InvoiceUploadService();
    }
    return this.invoiceUploadService;
  }

  public static getBookkeepingQaService(): BookkeepingQaService {
    if (!this.bookkeepingQaService) {
      this.bookkeepingQaService = new BookkeepingQaService();
    }
    return this.bookkeepingQaService;
  }

  public static getDocumentExportService(): DocumentExportService {
    if (!this.documentExportService) {
      this.documentExportService = new DocumentExportService();
    }
    return this.documentExportService;
  }

  public static getMessagingService(): MessagingService {
    if (!this.messagingService) {
      this.messagingService = new MessagingService();
    }
    return this.messagingService;
  }

  public static getEmailThreadService(): EmailThreadService {
    if (!this.emailThreadService) {
      this.emailThreadService = new EmailThreadService();
    }
    return this.emailThreadService;
  }

  public static getFeedbackService(): FeedbackService {
    if (!this.feedbackService) {
      this.feedbackService = new FeedbackService();
    }
    return this.feedbackService;
  }

  public static getProductEventsService(): ProductEventsService {
    if (!this.productEventsService) {
      this.productEventsService = new ProductEventsService();
    }
    return this.productEventsService;
  }

  public static getCardService(): CardService {
    if (!this.cardService) {
      this.cardService = new CardService();
    }
    return this.cardService;
  }
}
